import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { IJobsForDeleteFilter } from '../interfaces/inbox-job-filter';
import { ICancelData, ICancelOrderInfo } from '../interfaces/job/job';
import { IOrderInfo, IRescheduleOrders, ITaskInfo } from '../interfaces/task-info';
import { ITaskUpdate } from '../interfaces/task-update';
import { BaseDataService } from './base.data.service';
import { TaskPriceResult } from '../interfaces/customer-price-result/customer-price-result';
import { IRescheduleTaskContext } from '../interfaces/screen-specific/job-screen/vme/reschedule-task-context';
import { ICancellationStatus } from '../interfaces/cancellation-status';

@Injectable({ providedIn: 'root' })
export class OrderService extends BaseDataService {
  public url = '/api/order';

  //todo: back here, used forwardRef to bypass a circular ref in HttpResponsesHandlersFactory, happens elsewhere on this class so something bad is happening in there
  constructor(public http: HttpClient, protected handlersFactory: HttpResponsesHandlersFactory) {
    super(http, handlersFactory);
  }

  public get(): Promise<any> {
    return this.http
      .get(`${this.url}/list`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  public createOrderQuoteInvoice(quoteGuid: string) {
    return this.http.post(`/api/order-quote/${quoteGuid}/create-invoice`, {}).toPromise();
  }

  public regenTransitTimes(id: number): Promise<any> {
    return this.http.get(this.url + '/regenTransitTimes/' + id).toPromise();
  }

  public copy(orderId: number): Promise<IOrderInfo> {
    return this.http
      .get(`${this.url}/copy/` + orderId)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getlog(orderNumber: string): Promise<any> {
    return this.http
      .get(`${this.url}/${orderNumber}/log`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getlogsprice(sjorderNumber: string): Promise<any> {
    return this.http
      .get(`${this.url}/${sjorderNumber}/log/s`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getlogohmatching(jobnumber: string): Promise<any> {
    return this.http
      .get(`${this.url}/${jobnumber}/log/oh`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getlogecmatching(jobnumber: string): Promise<any> {
    return this.http
      .get(`${this.url}/${jobnumber}/log/ec`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getDump(id: number | string) {
    return this.http
      .get(`${this.url}/${id}/dump`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getOrderPriceLog(order: any): Promise<any> {
    return this.http
      .post(`${this.url}/customerpricelog`, order)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getCustomerPrice(order: any): Promise<any> {
    return this.http
      .post(`${this.url}/customerprice`, order)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  searchJobs(data: any): Promise<any[]> {
    return this.http
      .post(this.url + '/search', data)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getTasksForOrderDetails(orderId: number): Promise<any[]> {
    return this.http
      .get<any[]>(`${this.url}/getTasksForOrderDetails?orderId=${orderId}`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  updateTask(t: ITaskUpdate): Promise<ITaskInfo> {
    return this.http
      .post(`${this.url}/updateTask`, t)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  rescheduleOrders(t: IRescheduleOrders): Promise<any> {
    return this.http
      .post(`${this.url}/reschedule`, t)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getRescheduleContext(orderId: number): Promise<IRescheduleTaskContext[]> {
    return this.http
      .get(`${this.url}/${orderId}/reschedule-context`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  canSupplierMoveVehicle(t: ITaskUpdate): Promise<ITaskInfo> {
    return this.http
      .post(`${this.url}/canSupplierMoveVehicle`, t)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  cancelOrder(data: ICancelData): Promise<any> {
    return this.http.post(`${this.url}/cancelOrder`, data).toPromise();
  }

  getCancelOrderAction(orderId: number): Promise<number> {
    return this.http.get<number>(`${this.url}/getCancelOrderAction?id=${orderId}`).toPromise();
  }

  getCancelOrderInfo(orderId: number): Promise<ICancelOrderInfo> {
    return this.http
      .get<ICancelOrderInfo>(`${this.url}/getCancelOrderInfo?id=${orderId}`)
      .toPromise();
  }

  getCancelJobAction(jobId: number): Promise<number> {
    return this.http.get<number>(`${this.url}/getCancelJobAction?id=${jobId}`).toPromise();
  }

  cancelJob(data: ICancelData): Promise<any> {
    return this.http.post(`${this.url}/cancelJob`, data).toPromise();
  }

  getImpersonateSupplierUsers(jobId: number): Promise<any[]> {
    return this.http
      .get<any[]>(`${this.url}/getImpersonateSupplierUsers?jobId=${jobId}`)
      .toPromise();
  }

  getImpersonateCustomerUsers(orderId: number): Promise<any[]> {
    return this.http
      .get<any[]>(`${this.url}/getImpersonateCustomerUsers?orderId=${orderId}`)
      .toPromise();
  }

  getJobsForDeletePage(filter: IJobsForDeleteFilter): Promise<any> {
    let params = this.constructHttpParams(filter);
    return this.http.get(`${this.url}/getJobsForDelete`, { params: params }).toPromise();
  }

  deleteJobs(jobNumbers: string[]) {
    return this.http.post(`${this.url}/deleteJobs`, jobNumbers).toPromise();
  }

  getCancellationStatuses(): Promise<ICancellationStatus[]> {
    return this.http
      .get<any[]>(`${this.url}/cancellationStatuses`)
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getFullCancellationDescription(orderId): Promise<any> {
    return this.http.get(`${this.url}/fullCancellationDescription/${orderId}`).toPromise();
  }

  getPricesByTask(dto: ITaskInfo): Promise<TaskPriceResult> {
    return this.http.post<TaskPriceResult>(`${this.url}/getPricesByTask`, dto).toPromise();
  }

  public getForComponent<T>(id: number | string, component: string): Promise<T> {
    return this.http.get<T>(`/api/order/${id}/component/${component}`).toPromise();
  }

  //setReadMessageByVme(job: any) {
  //    return this.http.post(`${this.url}/setReadMessageByVme`, job).toPromise();
  //}
  quickCopy(id: number) {
    return this.http.post<any>(`${this.url}/${id}/quick-copy`, {}).toPromise();
  }
}
